import { Component, Inject, Injector } from '@angular/core';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { ElementTypeCategoriesDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout-external-settings';
import { DynamicLayoutBuilderService } from '@common-modules/dynamic-layout/layouts/dynamic-layout-builder-service';
import { SimpleDynamicLayoutSettings } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings';
import { SimpleDynamicLayoutSettingsItem } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings-item';
import { DynamicLayoutSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-settings';
import { GetValueSelector } from '@common-modules/dynamic-layout/state/generic/generic.selectors';
import { BaseDynamicWidgetComponent } from '@common-modules/redux/components/base-dynamic-widget.component';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { SelectedLayerInfo } from '@common-modules/wlm-map/models/selected-layer-info';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs/operators';
import { MasterOperatorConnectItemsSettings } from '../models/master-operator-connect-settings';
import { MasterOperatorFields } from '../models/master-operator-fields.enum';
import { ScenariosMetadata } from '../models/scenarios-metadata';
import { ScenarioInfoService } from '../services/scenario-info.service';

export class MasterOperatorAttributesSettings {
  singleId: string;
  connect: MasterOperatorConnectItemsSettings;
  layout: SimpleDynamicLayoutSettings;
}

const COMPONENT_SELECTOR = 'tc-master-operator-attributes-widget';
@UntilDestroy()
@Component({
    selector: COMPONENT_SELECTOR,
    templateUrl: './master-operator-attributes-widget.component.html',
    styleUrls: ['./master-operator-attributes-widget.component.scss'],
    providers: [ReduxStateService],
    standalone: false
})
export class MasterOperatorAttributesWidgetComponent extends BaseDynamicWidgetComponent {
  widgetInstanceKey: string;
  parentWidgetInstanceKey: string;
  layoutSettings: DynamicLayoutSettings;

  elementTypesCategories: Map<string, ElementTypeCategoriesDto[]>;

  private _selectedLayer: SelectedLayerInfo;
  public get selectedLayer(): SelectedLayerInfo {
    return this._selectedLayer;
  }
  public set selectedLayer(value: SelectedLayerInfo) {
    this._selectedLayer = value;

    if (value) {
      this.buildDynamicLayoutSettings();
    } else {
      this.layoutSettings = null;
    }
  }

  params: MasterOperatorAttributesSettings;

  readonly containerClass = 'dl-container-mo-attributes';

  readonly T_SCOPE = `${TranslateScopes.MasterOperator}.${COMPONENT_SELECTOR}`;
  readonly noAttrsTitleKey = `${this.T_SCOPE}.no-attributes-title`;
  readonly noAttrsSubtitleKey = `${this.T_SCOPE}.no-attributes-subtitle`;

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    private _dynamicLayoutBuilderService: DynamicLayoutBuilderService,
    private _scenarioInfoService: ScenarioInfoService
  ) {
    super(injector, widgetSettings);
    this.widgetInstanceKey = widgetSettings.widgetInstanceKey;
    this.parentWidgetInstanceKey = widgetSettings.params.parentWidgetInstanceKey;
    this.params = widgetSettings.params;
  }

  onWidgetInit(): void {
    this._state
      .select<SelectedLayerInfo>(
        new GetValueSelector({
          fieldName: MasterOperatorFields.Layer,
        })
      )
      .pipe(
        untilDestroyed(this),
        filter((data) => {
          return !!data && data.parentKey == this.parentWidgetInstanceKey;
        })
      )
      .subscribe((value) => {
        this.selectedLayer = value;
      });

    this._state
      .select<ScenariosMetadata>(
        new GetValueSelector({
          fieldName: MasterOperatorFields.AttributesMetadata,
        })
      )
      .pipe(
        untilDestroyed(this),
        filter((data) => {
          return !!data && data.singleId == this.parentWidgetInstanceKey;
        })
      )
      .subscribe((value: ScenariosMetadata) => {
        this.elementTypesCategories = value.metadata;
      });
  }

  get componentName(): string {
    return 'MasterOperatorAttributesWidgetComponent';
  }

  buildDynamicLayoutSettings(): void {
    if (this.layoutSettings) {
      this.layoutSettings = null;
    }

    if (!this.elementTypesCategories) {
      return;
    }

    const elementTypesCategories = this.elementTypesCategories[this.selectedLayer.key];

    if (!elementTypesCategories?.length) {
      return;
    }

    const elementType = elementTypesCategories.find((x) => x.type == this.selectedLayer.layerType);

    if (!elementType) {
      return;
    }

    const tabs = elementType.categories.map((category) => {
      const tabItem: SimpleDynamicLayoutSettingsItem = {
        titleKey: category.name,
        widgetName: 'MasterOperatorAttributeWidgetComponent',
        params: {
          category: category,
          selectedLayer: this.selectedLayer,
        },
      };

      return tabItem;
    });

    const simpleSettings = globalUtilsHelper.clone(this.params.layout, true);
    simpleSettings.items = tabs;

    this._dynamicLayoutBuilderService.build(simpleSettings, { generateWidgetIds: true }).subscribe({
      next: (settings) => {
        this.layoutSettings = settings;
      },
    });
  }

  getScenarioInfo = () => {
    return this.elementTypesCategories;
  };
}
